/** Parse and normalize number-like values. Returns null for invalid or empty input. */
export function parseNumeric(v: unknown): number | null {
  if (v === null || v === undefined) {
    return null;
  }
  const s = String(v).trim();
  if (!s) {
    return null;
  }
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Format earthquake magnitude:
 * - Round to 2 decimals if more exist
 * - Keep fewer decimals if already shorter
 * Examples: 1.0251 → 1.03, 3.50 → 3.5, 0.235 → 0.24
 * Note: uses epsilon-safe rounding to avoid IEEE-754 issues (e.g., 0.235 -> 0.23 with toFixed).
 */
export function formatMagnitude(v: unknown): string | null {
  const n = parseNumeric(v);
  if (n === null) {
    return null;
  }
  // Epsilon-safe rounding to 2 decimals to avoid cases like 0.235 -> 0.23 due to IEEE-754
  const rounded = Math.round((n + Number.EPSILON) * 100) / 100;
  return stripTrailingZeros(rounded.toFixed(2));
}

/**
 * Format depth in km:
 * - Values < 1 → round to 1 decimal (0.94 → 0.9, 0.06 → 0.1, 0.01 → 0)
 * - Values ≥ 1 → round to 0 decimals (1.1 → 1, 3.8 → 4)
 */
export function formatDepthKm(v: unknown): string | null {
  const n = parseNumeric(v);
  if (n === null) {
    return null;
  }
  if (n < 1) {
    const r = Math.round(n * 10) / 10;
    return r === 0 ? "0" : stripTrailingZeros(r.toFixed(1));
  }
  return String(Math.round(n));
}

/** Removes trailing zeros and redundant decimal points. */
export function stripTrailingZeros(numStr: string): string {
  // no "u" flag needed
  return numStr.replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
}
